// ============================================================================
// map-service.ts — All map logic from Map.ts, organized by concern
// Absorbed from MyScripts/Map.ts
// ============================================================================

import {
    INITIAL_MAP_ZOOM_LEVEL,
    POLYGON_OVERLAY_NORMAL_STROKE_OPACITY,
    POLYGON_OVERLAY_PATROL_ZONE_STROKE_OPACITY,
    NOT_VISIBLE_ICON_SVG,
    TEMP_MAP_STYLE,
    GOOGLE_NIGHT_MAP_STYLE,
    MIDNIGHT_MAP_STYLE,
    componentForm,
} from '@/constants/map-styles';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { AccessAuthorities } from '@/models';
import { hasValue, hasStringValue, getModuleType } from '@/utils/legacy-utils';

// ── External Declarations ───────────────────────────────────────────────────

const google = (window as any).google; // loaded by <script> tag before bundle
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';

import { getAvailableSubmodules, getAuthObject, getCurrentUserId } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';
import { appendCountrySuffix } from '@/utils/custom-location';

import { getCurrentThemeColor } from '@/services/theme-service';
import { THEME_NIGHT_BLUE_COOKIE_VALUE, THEME_NIGHT_GREY_COOKIE_VALUE } from '@/utils/theme';

// Lazy window-global lookups — resolved at call time, not module load time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getGeolocation = _wg('getGeolocation');
const startTurnByTurnNavigationV2 = _wg('startTurnByTurnNavigationV2');
const getCustomLocationOrCoordinatesFromAddressPromise = _wg('getCustomLocationOrCoordinatesFromAddressPromise');
const getTrustedLocationFromAddressPromise = _wg('getTrustedLocationFromAddressPromise');
const cleanArray = _wg('cleanArray');
interface CustomLocationOrCoordinates {
    type: any;
    latitude: number;
    longitude: number;
    [key: string]: any;
}
interface DataObjectLocationFieldsType {
    address: {
        formattedAddress: string;
        address: string;
        city: string;
        state: string;
        zip: string;
        country: string;
    };
    coordinates: {
        latitude: string;
        longitude: string;
    };
    customlocation: {
        customLocationId: string;
    };
    properties: {
        isOnlySavingCoordinatesOnCustomLocation: boolean;
    };
}

// ── Types & Classes ─────────────────────────────────────────────────────────

class KstPolygon extends google.maps.Polygon {
    id: number;
    label: any;
    isVisible: boolean;
    isPatrolZone: boolean;
    color: string;
}

class KstMapOverlayKml extends google.maps.KmlLayer {

}

type OrnaMapFiltersType = {
    isTrafficLayerEnabled: boolean,
    isTransitLayerEnabled: boolean,
    isBikeLayerEnabled: boolean,
    areOfficersAvailableEnabled: boolean,
    areOfficersOnCallEnabled: boolean,
    areCallCardsActiveEnabled: boolean,
    areCallCardsPendingEnabled: boolean,
    areGuardTourStopsCompleteEnabled: boolean,
    areGuardTourStopsClaimedEnabled: boolean,
    areGuardTourStopsUnclaimedEnabled: boolean
};

export interface MapController {
    // State properties (get/set)
    areMapOptionsShown: boolean;
    areMapOverlayOptionsShown: boolean;
    isEditingPolygonOverlay: boolean;
    isPolygonModified: boolean;
    isSelectedIsVisible: boolean;
    isSelectedPolygonPatrolZone: boolean;
    isSelectedPolygonEditable: boolean;
    isSelectedPolygonDraggable: boolean;
    selectedPolygonLabelText: string;
    selectedPolygonColor: string;
    addCustomLocationDialogLocation: string | null;
    addCustomLocationDialogCoordinates: string;
    showAddCustomLocationModal: boolean;
    showTrackMeButtons: boolean;
    isTrackingMe: boolean;
    showChangeCallLocationModal: boolean;
    pendingCallLocationChange: any;
    showDeleteAreaModal: boolean;
    showColorPicker: boolean;
    showKmlModal: boolean;
    // Methods
    openNav(): void;
    closeNav(): void;
    openOverlayOptions(): void;
    addPolygonOverlay(): void;
    removePolygonOverlay(): void;
    savePolygons(): void;
    onFilterCheckboxChange(e: any): void;
    onLayerSwitchChange(e: any): void;
    onTrackMeClick(): void;
    onCenterMeClick(): void;
    movePolygonOverlayToFront(): void;
    movePolygonOverlayToBack(): void;
    manageMapOverlayKml(): void;
    focusCustomLocationInput(): void;
}

class KMap {
    mapController: MapController | null = null;

    map: any;
    infoWindow: any;
    currentUserMarker: any;
    currentInfoWindow: any;
    personIdToPersonMarkerAndInfoWindowDict: { [key: string]: { marker: any, infowindow: any } } = {};
    callCardIdToCallCardMarkerAndInfoWindowDict: { [key: string]: { marker: any, infowindow: any } } = {};
    tourStopIdToTourStopMarkerAndInfoWindowDict: { [key: string]: { marker: any, infowindow: any } } = {};

    mapCachedResults: any = null;
    mapRefreshInterval: any = null;
    markerClusterer: any = null;
    markerSpiderfier: any = null;
    MARKER_CLUSTERER_MAX_ZOOM: any = 10;
    mapPrevZoom: any = 0;
    triggerFormatMarkers: boolean = false;
    currentEvent: any;
    originalMarkerPosition: any = null;
    centerMapOnYou: boolean = false;
    pauseRefresh: boolean = false;

    polygonOverlays: Array<KstPolygon> = [];
    selectedPolygonOverlay: KstPolygon = null;
    mapOverlayKmls: Array<KstMapOverlayKml> = []

    //Filters for availble officers, call cards, guard tour stops, and traffic/transit/bike layers.
    ornaMapFilters: OrnaMapFiltersType = null;

    // Layer objects for traffic, transit, and bike
    trafficLayer: google.maps.TrafficLayer = null;
    transitLayer: google.maps.TransitLayer = null;
    bikeLayer: google.maps.BicyclingLayer = null;

    isDoingMapListsApiCall = false;
}

// ── Singleton + Window Property ─────────────────────────────────────────────

let CurrentMap: KMap;

Object.defineProperty(window, 'CurrentMap', {
    get() { return CurrentMap; },
    set(val: KMap) { CurrentMap = val; },
    configurable: true,
    enumerable: true,
});

// ── Cleanup ─────────────────────────────────────────────────────────────────

function mapCleanup() {
    if (hasValue(CurrentMap)) {
        CurrentMap.personIdToPersonMarkerAndInfoWindowDict = {};
        CurrentMap.callCardIdToCallCardMarkerAndInfoWindowDict = {};
        CurrentMap.tourStopIdToTourStopMarkerAndInfoWindowDict = {};
        CurrentMap.mapCachedResults = null;
        if (CurrentMap.markerClusterer) {
            CurrentMap.markerClusterer.clearMarkers();
            CurrentMap.markerClusterer = null;
        }
        if (CurrentMap.markerSpiderfier) {
            CurrentMap.markerSpiderfier.removeAllMarkers();
            CurrentMap.markerSpiderfier = null;
        }
    }
}

// ── Entry Points (called by routing) ────────────────────────────────────────

function CAD_Map() {
    setupMap(false);
    initAutocompleteTemp();
    initMap(null, true, true);
    setUpMapOverlay();
    resetMapRefresh();
    setMapScreenLoaded(true);
}

function MDT_Map() {
    setupMap();

    initAutocompleteTemp();
    initMap(null, true, true);
    setUpMapOverlay();
    resetMapRefresh();
    setMapScreenLoaded(true);
}

function setupMap(showTrackMeButtons: boolean = true) {
    CurrentMap = new KMap();
    App._isMapCenterSet = false;

    // When MapScreen is rendered by the React router, initMapScreenReact
    // is a no-op (the component is already mounted). For legacy callers
    // that still use the window function, it mounts a standalone instance.
    (window as any).initMapScreenReact?.(showTrackMeButtons);
}

function setupMapCore() {
    CurrentMap = new KMap();
    App._isMapCenterSet = false;
}

// ── Overlay Management ──────────────────────────────────────────────────────

function getPatrolZoneNames(latitude: number, longitude: number, callback: (result: any) => void): void {
    getMapOverlay(true, (polygonOverlays: Array<KstPolygon>) => {
        const patrolZoneNames = [];
        for (let googleMapsPolygon of polygonOverlays) {
            if (googleMapsPolygon.isPatrolZone &&
                google.maps.geometry.poly.containsLocation(new google.maps.LatLng(latitude, longitude), googleMapsPolygon)) {
                patrolZoneNames.push(googleMapsPolygon.label.get("text"))
            }
        }
        return callback(patrolZoneNames);
    });
}

function getAllPolygonLabelsAndIds(callback: (result: any) => void): void {
    getMapOverlay(true, (polygonOverlays: Array<KstPolygon>) => {
        const labelsAndIds = [];
        for (let googleMapsPolygon of polygonOverlays) {
            labelsAndIds.push({ label: googleMapsPolygon.label?.text ?? "N/A", id: googleMapsPolygon.id })
        }
        return callback(labelsAndIds);
    });
}

function updateMapOverlayKml(useCache: boolean) {
    getMapOverlayKml(useCache, (mapOverlayKmls: Array<KstMapOverlayKml>) => {
        if (!hasValue(CurrentMap)) return;

        if (hasValue(CurrentMap.mapOverlayKmls) && CurrentMap.mapOverlayKmls.length > 0) {
            for (let overlay of CurrentMap.mapOverlayKmls) {
                overlay.setMap(null);
            }
        }

        CurrentMap.mapOverlayKmls = mapOverlayKmls;

        let submodules: Array<string> = getAvailableSubmodules();
        if (submodules === null) {
            submodules = [];
        }
        for (let mapOverlayKml of mapOverlayKmls) {
            mapOverlayKml.setMap(CurrentMap.map);
        }
    });
}

function updateMapOverlay(useCache: boolean) {
    getMapOverlay(useCache, (polygonOverlays: Array<KstPolygon>) => {
        if (!hasValue(CurrentMap)) return;

        if (hasValue(CurrentMap.polygonOverlays) && CurrentMap.polygonOverlays.length > 0) {
            for (let polygon of CurrentMap.polygonOverlays) {
                polygon.setMap(null);
                polygon.label.setMap(null);
            }
        }

        CurrentMap.polygonOverlays = polygonOverlays;

        let submodules: Array<string> = getAvailableSubmodules();
        if (submodules === null) {
            submodules = [];
        }
        const canViewInvisiblePolygons = isInArray(submodules, AccessAuthorities.ROLE_MAP_OVERLAY_AREA_VISIBILITY);
        for (let googleMapsPolygon of polygonOverlays) {
            // If user doesn't have permission to view invisible polygons, don't show them. If user does have the permission, show the invisible polygons.
            if (googleMapsPolygon.isVisible || canViewInvisiblePolygons) {
                googleMapsPolygon.setMap(CurrentMap.map);
                googleMapsPolygon.label.setMap(CurrentMap.map);
            }
        }

        resetOverlayZIndexes();
    });
}

function resetOverlayZIndexes() {
    const zIndexOffset = -1000000 //Have the polygons below anything else like pins for calls
    CurrentMap.polygonOverlays.forEach((overlay, i) => {
        overlay.setOptions({
            zIndex: i + 1 + zIndexOffset
        });
    });
}

function realtimeUpdateMapOverlay() {
    updateMapOverlay(false);
    updateMapOverlayKml(false);
}

function setUpMapOverlay() {
    updateMapOverlay(true);
    updateMapOverlayKml(true);
}

let _polygonOverlays = null;
function clearPolygonOverlayCache() {
    _polygonOverlays = null;
}
let _mapOverlayKmls = null;
function clearMapOverlayKmlCache() {
    _mapOverlayKmls = null;
}

function getMapOverlay(useCache: boolean, successCallback: (result: any) => void) {
    if (useCache && _polygonOverlays) {
        successCallback(_polygonOverlays);
        return;
    }
    api.getMapOverlay((result) => {
        _polygonOverlays = [];
        for (let overlay of result.polygonOverlays) {
            //Last point of polygon is the same as first, so remove it. This is a requirement from the backend.
            overlay.polygon.pop();
            const googleMapsPolygon = newGoogleMapsPolygon(overlay.id, overlay.polygon, overlay.label, overlay.isPatrolZone, overlay.isVisible, false, '#' + overlay.color);
            _polygonOverlays.push(googleMapsPolygon);
        }
        successCallback(_polygonOverlays);
    }, (error) => {
        //Only show this error if user is logged in.
        if (getAuthObject()) {
            App.showPopupNotification("Error getting map overlay.", "error");
        }
    });
}

function getMapOverlayKml(useCache: boolean, successCallback: (result: any) => void) {
    if (useCache && _mapOverlayKmls) {
        successCallback(_mapOverlayKmls);
        return;
    }
    api.getMapOverlayKmlList((result) => {
        _mapOverlayKmls = []
        for (let attachment of result.attachmentList) {
            const kmlLayer = new google.maps.KmlLayer({
                url: attachment.signedURL,
                suppressInfoWindows: false,
                map: null //CurrentMap.map
            });
            _mapOverlayKmls.push(kmlLayer);
        }
        successCallback(_mapOverlayKmls);
    }, (error) => {
        //Only show this error if user is logged in.
        if (getAuthObject()) {
            App.showPopupNotification("Error getting map overlay KML.", "error");
        }
    });
}

// ── Polygon Construction ────────────────────────────────────────────────────

function calculateMapLabelSizes(zoom: number) {
    const baseFontSize = 12;
    const baseIconSize = 8;
    const baseZoom = 10;
    const scaleFactor = Math.pow(2, (zoom - baseZoom) * 0.15);

    return {
        fontSize: Math.max(6, Math.min(30, baseFontSize * scaleFactor)),
        iconSize: Math.max(4, Math.min(20, baseIconSize * scaleFactor))
    };
}

function newGoogleMapsPolygon(id: number, latLngArr: Array<google.maps.LatLngLiteral>, label: string, isPatrolZone: boolean, isVisible: boolean, isEditable: boolean, color: string): KstPolygon {
    const polygon = new KstPolygon({
        paths: latLngArr,
        strokeColor: color,
        strokeOpacity: isPatrolZone ? POLYGON_OVERLAY_PATROL_ZONE_STROKE_OPACITY : POLYGON_OVERLAY_NORMAL_STROKE_OPACITY,
        strokeWeight: 5,
        fillColor: color,
        fillOpacity: 0.25,
        editable: isEditable,
        //draggable: true
    });

    //CurrentMap may not be defined yet e.g. if called via getPatrolZoneNames().
    const sizes = calculateMapLabelSizes(CurrentMap?.map?.getZoom() ?? INITIAL_MAP_ZOOM_LEVEL);
    const mapLabel = new (window as any).MapLabel({
        text: label,
        position: getPolygonCenter(polygon),
        fontSize: sizes.fontSize,
        align: 'center',
        icon: isVisible ? null : NOT_VISIBLE_ICON_SVG,
        iconSize: sizes.iconSize
    });
    polygon.id = id;
    polygon.label = mapLabel;
    polygon.isVisible = isVisible;
    polygon.isPatrolZone = isPatrolZone;
    polygon.color = color;

    google.maps.event.addListener(polygon, 'rightclick', function (event) {
        if (getModuleType() === "cad") {
            //Polygon click events prevent click events on the map itself.
            mapRightClickedInCad(event.latLng);
        }
    });

    google.maps.event.addListener(polygon, 'click', function () {
        if (getModuleType() === "cad") {
            //Polygon click events prevent click events on the map itself.
            mapLeftClickedOrDraggedInCad();
        }

        //Don't do anything if the map overlay options aren't shown.
        if (!CurrentMap.mapController!.areMapOverlayOptionsShown) {
            return;
        }

        //Don't do anything if we're clicking the currently selected polygon.
        if (CurrentMap.selectedPolygonOverlay === this) {
            return;
        }

        deselectAllPolygons();
        this.setOptions({ strokeWeight: 5, strokeOpacity: polygon.isPatrolZone ? POLYGON_OVERLAY_PATROL_ZONE_STROKE_OPACITY : POLYGON_OVERLAY_NORMAL_STROKE_OPACITY });

        CurrentMap.mapController!.isSelectedIsVisible = this.isVisible;
        CurrentMap.mapController!.isSelectedPolygonPatrolZone = this.isPatrolZone;
        CurrentMap.mapController!.isSelectedPolygonEditable = true;
        CurrentMap.mapController!.isSelectedPolygonDraggable = false;
        CurrentMap.mapController!.selectedPolygonColor = this.color;
        //Need to set the options here due to race condition.
        this.setEditable(true);
        this.setDraggable(false);

        if (this.label) {
            CurrentMap.mapController!.selectedPolygonLabelText = this.label.get("text");
        }

        CurrentMap.mapController!.isEditingPolygonOverlay = true;
        CurrentMap.selectedPolygonOverlay = this;
    });

    function updatePolygonLabelPosition(polygon: KstPolygon) {
        if (polygon.label) {
            polygon.label.set("position", getPolygonCenter(polygon));
        }
    }

    google.maps.event.addListener(polygon, 'drag', function () {
        updatePolygonLabelPosition(this);
    });

    const paths = polygon.getPaths();
    for (let i = 0; i < paths.getLength(); i++) {
        const path = paths.getAt(i);
        path.addListener('insert_at', function (index, obj) {
            CurrentMap.mapController!.isPolygonModified = true;
            updatePolygonLabelPosition(polygon);
        });
        path.addListener('set_at', function (index, obj) {
            CurrentMap.mapController!.isPolygonModified = true;
            updatePolygonLabelPosition(polygon);
        });
        path.addListener('remove_at', function (index, obj) {
            CurrentMap.mapController!.isPolygonModified = true;
            updatePolygonLabelPosition(polygon);
        });
    }

    return polygon;
}

function mapRightClickedInCad(latLng) {
    CurrentMap.currentEvent = latLng;
    showContextMenu(latLng);
}

function mapLeftClickedOrDraggedInCad() {
    document.querySelectorAll(".contextmenu").forEach(el => el.remove());
}

function deselectAllPolygons() {
    for (let polygon of CurrentMap.polygonOverlays) {
        polygon.setOptions({ strokeWeight: 2, strokeOpacity: polygon.isPatrolZone ? POLYGON_OVERLAY_PATROL_ZONE_STROKE_OPACITY : POLYGON_OVERLAY_NORMAL_STROKE_OPACITY });
        polygon.setEditable(false);
        polygon.setDraggable(false);
    }
    CurrentMap.selectedPolygonOverlay = null;
    CurrentMap.mapController!.isEditingPolygonOverlay = false;
}

function getPolygonCenter(polygon: KstPolygon): google.maps.LatLng {
    const paths = polygon.getPaths();
    let path;
    const coordsXY = []
    for (var i = 0; i < paths.getLength(); i++) {
        path = paths.getAt(i);
        for (var ii = 0; ii < path.getLength(); ii++) {
            const coord: google.maps.LatLng = path.getAt(ii);
            coordsXY.push({ x: coord.lat(), y: coord.lng() })
        }
    }

    const centerXY = get_polygon_centroid(coordsXY);
    return new google.maps.LatLng(centerXY.x, centerXY.y);
}

function get_polygon_centroid(pts) {
    //From https://stackoverflow.com/a/43747218/443044
    var first = pts[0], last = pts[pts.length - 1];
    if (first.x != last.x || first.y != last.y) pts.push(first);
    var twicearea = 0,
        x = 0, y = 0,
        nPts = pts.length,
        p1, p2, f;
    for (var i = 0, j = nPts - 1; i < nPts; j = i++) {
        p1 = pts[i]; p2 = pts[j];
        f = (p1.y - first.y) * (p2.x - first.x) - (p2.y - first.y) * (p1.x - first.x);
        twicearea += f;
        x += (p1.x + p2.x - 2 * first.x) * f;
        y += (p1.y + p2.y - 2 * first.y) * f;
    }
    f = twicearea * 3;
    return { x: x / f + first.x, y: y / f + first.y };
}

// ── Polling ─────────────────────────────────────────────────────────────────

function clearMapRefresh() {
    if (App.IsNOU(CurrentMap) === false && App.IsNOU(CurrentMap.mapRefreshInterval) === false) {
        clearInterval(CurrentMap.mapRefreshInterval); // Will clear the timer.
        CurrentMap.mapRefreshInterval = null;
    }
}

function resetMapRefresh() {
    clearMapRefresh();
    CurrentMap.mapRefreshInterval = setInterval(() => {
        //const centerMapOnYou: boolean = App.moduleTypeFromURL === "mdt";
        setOfficerAndCallCardLocations(CurrentMap.map, CurrentMap.centerMapOnYou, true, false);
    }, 6000); //updates map once every 6s
}

function updateMapFromEvent() {
    resetMapRefresh();
    setOfficerAndCallCardLocations(CurrentMap.map, false, true, false);
}

// ── Map State Helpers ───────────────────────────────────────────────────────

function isMapScreenLoaded(): boolean {
    return App._isMapScreenLoaded;
}

function isMapCenterSet(): boolean {
    return App._isMapCenterSet;
}

function cleanUpMap(): void {
    App._isMapCenterSet = false
}

function setMapScreenLoaded(isMapScreenLoaded: boolean): void {
    App._isMapScreenLoaded = isMapScreenLoaded;
}

function currentMapSetCenter(coords): void {
    App._isMapCenterSet = true;
    CurrentMap.map.setCenter(coords);
}

function trackUser() {
    const w = window as any;
    if (w.isUWPApp || w.isIOSApp || w.isAndroidApp || w.isWindowsAppSdkApp) {
        //apps do tracking based on select module and logout
        return;
    }

    if (getModuleType() === "mdt") {
        getGeolocation(trackUserSuccessCallback, trackUserFailCallback);
    }
}

function trackUserSuccessCallback(position: any) {
    //Do another check for if the module type is "mdt" in case we switched modules between
    // initiating geolocation check and sending location to server.
    if (position !== null && position != undefined && getModuleType() === "mdt") {
        api.trackUser(position.coords["latitude"], position.coords["longitude"], result => {
        }, errorResult => {
        });
    }
}

function trackUserFailCallback(err: any) {
}

function centerMapOnCurrentLocation() {
    getGeolocation(centerMapOnCurrentLocationSuccess, centerMapOnCurrentLocationFail);
}

function centerMapOnCurrentLocationSuccess(position: any) {
    const pos = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
    };

    currentMapSetCenter(pos);
}

function centerMapOnCurrentLocationFail(err: any) {
    if (!isMapCenterSet()) {
        currentMapSetCenter(new google.maps.LatLng(41.850033, -87.6500523));
        CurrentMap.map.setZoom(3);
    }
}

// ── Map Initialization ──────────────────────────────────────────────────────

/**
 * Draw the map for an address. Resolution order, cheapest and most certain first: the record's
 * stored coordinate, a custom location or literal coordinate, a trusted client/site coordinate,
 * then a geocode tagged source='render' (re-geocoding a stored call can only make it worse).
 *
 * @param context the displayed record's stored lat/lng, its companyId for the trusted-location
 *                lookup, and its addressVerificationStatus so an uncertain pin renders as one.
 */
function initMap(formattedAddress: string, centerMapOnYou: boolean, authTokenRefresh: boolean, addStandaloneMarker: boolean = false,
                 context?: { lat?: number | null, lng?: number | null, companyId?: number | null, addressVerificationStatus?: string | null }) {
    // The uncertainty ring draws even when the live marker pipeline owns the pin itself.
    const placeAt = (pos: { lat: number, lng: number }, certainty: PinCertainty = null) => {
        if (addStandaloneMarker) {
            addStandaloneLocationMarker(CurrentMap.map, pos, certainty);
        } else {
            currentMapSetCenter(pos);
        }
        if (certainty === 'approximate') {
            drawApproximateUncertaintyCircle(CurrentMap.map, pos);
        }
    };

    // Strict finite-number check rather than hasNumericValue(), which returns true for the empty
    // string (isNaN('') is false). An empty coordinate reaching Number() would centre the map on
    // (0, 0) — the Gulf of Guinea — instead of falling through to the lookups below.
    const isCoordinate = (v: any): boolean => typeof v === 'number' && Number.isFinite(v);
    const storedLat = context?.lat;
    const storedLng = context?.lng;

    if (formattedAddress !== null && isCoordinate(storedLat) && isCoordinate(storedLng)) {
        placeAt({ lat: Number(storedLat), lng: Number(storedLng) }, pinCertaintyForStatus(context?.addressVerificationStatus));
    } else if (formattedAddress !== null) {
        getCustomLocationOrCoordinatesFromAddressPromise(formattedAddress).then((customLocationOrCoordinates: CustomLocationOrCoordinates) => {
            if (customLocationOrCoordinates.type === (window as any).CustomLocationOrCoordinatesType.CUSTOM_LOCATION || customLocationOrCoordinates.type === (window as any).CustomLocationOrCoordinatesType.COORDINATES) {
                placeAt({
                    lat: customLocationOrCoordinates.latitude,
                    lng: customLocationOrCoordinates.longitude
                });
                return;
            }
            // A client or site whose pin is already trusted answers without a geocode, the same
            // way it does on the address-entry path.
            const geocodeFallback = () => {
                //Nothing stored, nothing custom, nothing trusted — ask the geocoder.
                geocodeLocation(App.getGeocoder(), formattedAddress, results => {
                    const geocoderResults = results.geocoderResults;
                    if (hasValue(geocoderResults) && geocoderResults.length > 0) {
                        // A render-time geocode below high confidence is as uncertain as a stored LOW pin.
                        const renderConfidence = (results as any).confidence != null
                            ? String((results as any).confidence).toLowerCase()
                            : null;
                        placeAt({
                            lat: geocoderResults[0].geometry.location.lat(),
                            lng: geocoderResults[0].geometry.location.lng()
                        }, renderConfidence != null && renderConfidence !== 'high' ? 'approximate' : null);
                    } else {
                        const errMessage: string = `Geocode was not successful for initMap() with formattedAddress "${formattedAddress}". "results": ${JSON.stringify(results)}`;
                        api.insertNewJSError(errMessage, null, null, (window as any).JSErrorType.CAUGHT, null);
                    }
                }, { companyId: context?.companyId ?? undefined, source: 'render' });
            };
            // A trusted-location lookup is an optimisation; it must never be able to leave the map
            // blank. Any failure falls through to the geocoder exactly as if nothing matched.
            getTrustedLocationFromAddressPromise(formattedAddress, context?.companyId ?? undefined, null)
                .then((trusted: any) => {
                    if (trusted && isCoordinate(Number(trusted.latitude)) && isCoordinate(Number(trusted.longitude))) {
                        placeAt({ lat: Number(trusted.latitude), lng: Number(trusted.longitude) });
                        return;
                    }
                    geocodeFallback();
                })
                .catch(() => { geocodeFallback(); });
        });
    }

    CurrentMap.map.addListener('click', function () {
        setTimeout(() => {
            deselectAllPolygons();
            updateInfoWindowCSS();
        }, 0);
    });
    setOfficerAndCallCardLocations(CurrentMap.map, centerMapOnYou, true, authTokenRefresh);
}

// ── Geocoding / Address ─────────────────────────────────────────────────────

function isLatLongCoords(data: string): boolean {
    //from https://stackoverflow.com/a/18690202/443044
    return /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/.test(data);
}

//returns object with properties:
// geocoderResults - list of GeocoderResult (see https://developers.google.com/maps/documentation/javascript/geocoding)
// locationType - either "location" if the input is a lat/long pair, or "address" if the input is an address
// locationCoords - only used if locationType === "location" . It is a lat/long pair. It is an object with two properties of type number: lat and lng.
/**
 * Geocoding entry point.
 *
 * Behavior is controlled by App.useGeocodingProxy():
 *   - When true: routes through the Spring backend at POST /api/{ver}/{role}/geocode,
 *     which calls Geocodio server-side. The backend's normalized response is translated
 *     back into a Google-shaped GeocodedLocation so all downstream code keeps working.
 *   - When false: uses the legacy google.maps.Geocoder path. Kept during rollout so the
 *     feature flag can be flipped off if issues arise; removed once the rollout is complete.
 *
 *   geocoder - Google geocoder instance (App.getGeocoder()). Unused on the proxy path but
 *              kept in the signature so existing callers don't have to change.
 *   location - address string OR "lat,lng" string OR { lat, lng } object
 *   callback - invoked with a GeocodedLocation
 *   context  - optional { companyId, previousLat, previousLng, source, alternateIndex }.
 *              companyId is used for biasing the geocode to a specific call's company
 *              (supports cross-company dispatchers). previousLat/Lng enable server-side
 *              discrepancy detection on address edits. source is telemetry only — 'entry' when
 *              a person is typing an address, 'render' when a map is drawing a stored one — and
 *              without it the two are indistinguishable in the miss numbers.
 */
function geocodeLocation(geocoder: any, location: any, callback: any, context?: { companyId?: number, previousLat?: number, previousLng?: number, source?: string }): void {
    if (!hasValue(callback) || typeof callback !== "function") {
        api.insertNewJSError(`geocodeLocation callback error`, null, null, (window as any).JSErrorType.UNCAUGHT, null);
        App.showStandardErrorPopupNotification();
        return;
    }

    // Normalize: track whether the input was a coords pair so the GeocodedLocation reflects it.
    // The backend handles both forms in the same endpoint and returns the same response shape.
    let locationType: string;
    let normalizedCoords: any = null;
    let queryString: string;
    if (typeof location === 'object' && location !== null && 'lat' in location && 'lng' in location) {
        normalizedCoords = { lat: Number(location.lat), lng: Number(location.lng) };
        queryString = `${normalizedCoords.lat},${normalizedCoords.lng}`;
        locationType = 'location';
    } else if (isLatLongCoords(location)) {
        const latLongArr = (location as string).replace(/[\(\)]/g, "").split(",");
        normalizedCoords = { lat: Number(latLongArr[0]), lng: Number(latLongArr[1]) };
        queryString = `${normalizedCoords.lat},${normalizedCoords.lng}`;
        locationType = 'location';
    } else {
        queryString = location as string;
        locationType = 'address';
    }

    if (App.useGeocodingProxy()) {
        const params: any = { query: queryString };
        if (context?.companyId != null) params.companyId = context.companyId;
        if (context?.previousLat != null) params.previousLat = context.previousLat;
        if (context?.previousLng != null) params.previousLng = context.previousLng;
        // Default to 'entry': every caller that does not say otherwise is a person typing.
        params.source = context?.source ?? 'entry';
        // Entry lookups auto-accept a below-high pin without a modal; render lookups accept nothing.
        if (params.source === 'entry') params.acceptsUnconfirmed = true;

        api.geocodeViaProxy(
            params,
            (proxyResult: any) => {
                const geocoderResults = translateProxyResponseToGoogleShape(proxyResult);
                const returnVal: GeocodedLocation = new (window as any).GeocodedLocation({
                    geocoderResults,
                    locationType,
                    locationCoords: normalizedCoords ?? queryString,
                });
                // Surface backend confidence + discrepancy on the result so the
                // inline preview component can render the appropriate UI without re-deriving.
                (returnVal as any).confidence = proxyResult?.confidence ?? 'none';
                (returnVal as any).discrepancyFromPrevious = proxyResult?.discrepancyFromPrevious === true;
                (returnVal as any).accuracyType = proxyResult?.accuracyType ?? null;
                (returnVal as any).accuracyScore = proxyResult?.accuracyScore ?? null;
                (returnVal as any).learnedAddressId = proxyResult?.learnedAddressId ?? null;
                // 'none' because the lookup services could not be reached, not because the address didn't match.
                (returnVal as any).lookupUnavailable = proxyResult?.lookupUnavailable === true;
                callback(returnVal);
            },
            () => {
                // Network / backend failure — same handling as Google's catch path
                const returnVal: GeocodedLocation = new (window as any).GeocodedLocation({
                    geocoderResults: null,
                    locationType,
                    locationCoords: normalizedCoords ?? queryString,
                });
                (returnVal as any).confidence = 'none';
                (returnVal as any).lookupUnavailable = true;
                callback(returnVal);
            }
        );
        return;
    }

    // Legacy Google path — retained for the feature-flag rollout window
    if (!hasValue(geocoder)) {
        api.insertNewJSError(`geocodeLocation called without geocoder on legacy path`, null, null, (window as any).JSErrorType.UNCAUGHT, null);
        App.showStandardErrorPopupNotification();
        return;
    }
    const geocodeParams: any = {};
    geocodeParams[locationType] = normalizedCoords ?? queryString;
    geocoder.geocode(geocodeParams).then((response) => {
        const returnVal: GeocodedLocation = new (window as any).GeocodedLocation({ geocoderResults: response.results, locationType, locationCoords: normalizedCoords ?? queryString });
        callback(returnVal);
    }).catch(() => {
        const returnVal: GeocodedLocation = new (window as any).GeocodedLocation({ geocoderResults: null, locationType, locationCoords: normalizedCoords ?? queryString });
        callback(returnVal);
    });
}

/**
 * Translate the backend GeocodeResponse into the Google-shaped result array that downstream
 * code (fillInAddress, fillInAddressFromSuggestions) already knows how to consume.
 *
 * Returns null when the backend reported confidence=none with no usable result (mirrors
 * Google's "no match" failure mode so existing branches stay correct).
 */
function translateProxyResponseToGoogleShape(proxy: any): any[] | null {
    if (!proxy) return null;
    const top = proxy;
    // Defensive lowercase — backend currently returns lowercase 'high'/'low'/'none' but a future
    // normalization to uppercase shouldn't silently downgrade results here.
    const topConfidence: string | null = top?.confidence != null ? String(top.confidence).toLowerCase() : null;
    if (!top || topConfidence === 'none' || top.lat == null || top.lng == null) {
        return null;
    }

    // Geocodio omits the country in formatted_address; restore the ", USA" / ", Canada" suffix
    // here so pilot results look identical to legacy Google results and to existing DB data.
    // Alternates fall back to the top result's country (Geocodio only spans US + CA, so a top
    // result country is a reasonable proxy when an alternate's country isn't on the wire).
    const topCountry: string | null = top?.components?.country ?? null;

    const buildResult = (formatted: string, lat: number, lng: number, components: any) => {
        const lng_ = Number(lng);
        const lat_ = Number(lat);
        return {
            formatted_address: appendCountrySuffix(formatted ?? '', components?.country ?? topCountry),
            geometry: {
                location: {
                    // map-service.ts:909-911 calls .lat() / .lng() as functions on Google's LatLng.
                    // Keep the contract by returning functions here.
                    lat: () => lat_,
                    lng: () => lng_,
                }
            },
            address_components: buildAddressComponents(components),
        };
    };

    const results: any[] = [buildResult(top.formatted, top.lat, top.lng, top.components)];
    if (Array.isArray(top.alternates)) {
        for (const alt of top.alternates) {
            if (alt?.lat != null && alt?.lng != null) {
                results.push(buildResult(alt.formatted, alt.lat, alt.lng, null));
            }
        }
    }
    return results;
}

/**
 * Build a Google-style address_components array from the backend's flat components object.
 *
 * fillInAddress() at line ~852 iterates these looking for specific types:
 *   street_number, route, locality, sublocality_level_1, administrative_area_level_1, postal_code, country.
 * It also reads either long_name or short_name based on `componentForm[type]`. We populate both to the
 * same value since the backend gives us a single string per component.
 */
function buildAddressComponents(components: any): any[] {
    if (!components) return [];
    const result: any[] = [];
    const push = (types: string[], value: string | null | undefined) => {
        if (value == null || value === '') return;
        result.push({ types, long_name: value, short_name: value });
    };
    push(['street_number'], components.number);
    push(['route'], components.street);
    push(['locality', 'political'], components.city);
    push(['administrative_area_level_1', 'political'], components.state);
    push(['postal_code'], components.zip);
    push(['country', 'political'], components.country);
    return result;
}

/**
 * How certain a call's pin is. 'approximate' = the geocoder's best guess for the address (LOW);
 * 'area' = the address was not found and the pin is the company's operating-area centre (AREA).
 */
type PinCertainty = 'approximate' | 'area' | null;

const APPROXIMATE_PIN_LABEL = 'Approximate location';
const AREA_PIN_LABEL = 'The pin marks the service area, not the address.';

function pinCertaintyForStatus(status: string | null | undefined): PinCertainty {
    if (status === 'LOW') return 'approximate';
    if (status === 'AREA') return 'area';
    return null;
}

/** Droplet path drawn around (0,0) with the tip at the anchor point. */
const PIN_DROPLET_PATH = 'M 0,0 C -2,-8 -10,-11 -10,-19 A 10,10 0 1,1 10,-19 C 10,-11 2,-8 0,0 z';

/**
 * Icon + label for an uncertain pin: an amber droplet marked "≈" for an approximate pin, a hollow
 * amber droplet marked "?" for a service-area pin. prefix lets the live board keep its A/P letter.
 */
function uncertainPinStyle(certainty: 'approximate' | 'area', prefix: string = ''): { icon: any, label: any, title: string } {
    const area = certainty === 'area';
    return {
        icon: {
            path: PIN_DROPLET_PATH,
            fillColor: area ? '#ffffff' : '#f59e0b',
            fillOpacity: 1,
            strokeColor: area ? '#b45309' : '#7c5806',
            strokeWeight: area ? 2 : 1.5,
            scale: 1.4,
            labelOrigin: new google.maps.Point(0, -19),
        },
        label: {
            text: prefix + (area ? '?' : '≈'),
            color: area ? '#b45309' : '#3b2905',
            fontWeight: '700',
            fontSize: prefix ? '11px' : '13px',
        },
        title: area ? AREA_PIN_LABEL : APPROXIMATE_PIN_LABEL,
    };
}

/**
 * Drop a single static pin where the live marker pipeline will not draw one (a closed CAD call,
 * or a plain address). A verified pin is Google's default red marker; an uncertain one uses
 * uncertainPinStyle so it can never be mistaken for a verified location.
 */
function addStandaloneLocationMarker(resultsMap: any, pos: { lat: number, lng: number }, certainty: PinCertainty = null) {
    if (hasValue(pos)) {
        const marker = new google.maps.Marker(certainty
            ? { position: pos, ...uncertainPinStyle(certainty) }
            : { position: pos });

        marker.setMap(resultsMap);
        resultsMap.setCenter(pos);
    }
}

/**
 * A soft amber ring that reads as "somewhere around here" for an approximate pin. Not drawn for a
 * service-area pin: a 150 m ring around the area centre would claim a precision it does not have.
 */
function drawApproximateUncertaintyCircle(resultsMap: any, pos: { lat: number, lng: number }) {
    if (!hasValue(pos)) {
        return;
    }
    new google.maps.Circle({
        map: resultsMap,
        center: pos,
        radius: 150,
        strokeColor: '#f59e0b',
        strokeOpacity: 0.6,
        strokeWeight: 1.5,
        fillColor: '#f59e0b',
        fillOpacity: 0.08,
        clickable: false,
    });
}

function fillInAddressFromSuggestions(locationResultsObj: any, currentDataObject, locationField: HTMLInputElement | null,
    dataObjectLocationFields: DataObjectLocationFieldsType, focusOnLocationField: boolean = true) {

    if (locationResultsObj.type === (window as any).CustomLocationOrCoordinatesType.CUSTOM_LOCATION) {
        fillInCustomLocationAndCoordinatesAddress(locationResultsObj, currentDataObject, locationField, dataObjectLocationFields, focusOnLocationField);
        return;
    }
    if (locationResultsObj.type === (window as any).CustomLocationOrCoordinatesType.COORDINATES) {
        fillInCustomLocationAndCoordinatesAddress(locationResultsObj, currentDataObject, locationField, dataObjectLocationFields, focusOnLocationField);
        return;
    }

    const geocoderResults = locationResultsObj.geocoderResults;
    if (hasValue(geocoderResults) && geocoderResults.length > 0) {
        // Propagate the proxy-path confidence/userAdjusted fields onto the per-result object so
        // fillInAddress() can derive addressVerificationStatus for the entity.
        const locationResultObj = {
            geocoderResult: geocoderResults[0],
            locationType: locationResultsObj.locationType,
            locationCoords: locationResultsObj.locationCoords,
            confidence: (locationResultsObj as any).confidence,
            userAdjusted: (locationResultsObj as any).userAdjusted === true,
            pinnedToArea: (locationResultsObj as any).pinnedToArea === true,
            learnedAddressId: (locationResultsObj as any).learnedAddressId ?? null,
        };
        fillInAddress(locationResultObj, currentDataObject, locationField, dataObjectLocationFields, focusOnLocationField);
    } else {
        //If no geocoder results, clear address info.
        clearAddressInfo(currentDataObject, dataObjectLocationFields);
        setLearnedAddressId(currentDataObject, null);
    }
}

// Only call cards carry the learned-location link; other entities never gain the field.
function setLearnedAddressId(currentDataObject: any, learnedAddressId: number | null) {
    if (currentDataObject && 'learnedAddressId' in currentDataObject) {
        currentDataObject.learnedAddressId = learnedAddressId;
    }
}

function clearAddressInfo(currentDataObject, dataObjectLocationFields) {
    const alwaysEmptyFields = cleanArray([
        dataObjectLocationFields.address.address,
        dataObjectLocationFields.address.city,
        dataObjectLocationFields.address.state,
        dataObjectLocationFields.address.zip,
        dataObjectLocationFields.address.country,
    ]);

    for (const field of alwaysEmptyFields) {
        if (field) {
            currentDataObject[field] = '';
        }
    }
}

//locationResultsObj set in validateAddressWithSuggestions()
function fillInCustomLocationAndCoordinatesAddress(locationResultsObj, currentDataObject, locationField: HTMLInputElement | null,
    dataObjectLocationFields: DataObjectLocationFieldsType, focusOnLocationField: boolean = true) {

    //Address, city, state, zip, and country are always '' with custom locations.
    clearAddressInfo(currentDataObject, dataObjectLocationFields);

    currentDataObject[dataObjectLocationFields.address.formattedAddress] = locationResultsObj.location;
    if (locationField) locationField.value = locationResultsObj.location;
    currentDataObject[dataObjectLocationFields.coordinates.latitude] = locationResultsObj.latitude;
    currentDataObject[dataObjectLocationFields.coordinates.longitude] = locationResultsObj.longitude;
    currentDataObject[dataObjectLocationFields.customlocation.customLocationId] = hasValue(locationResultsObj.id) ? locationResultsObj.id : null;
    setLearnedAddressId(currentDataObject, null);
    // Custom locations and pure-coordinates inputs reflect an explicit user choice — mark MANUAL
    // so the supervisor dashboard / verification status reflects the intent.
    // Gated to pilot users: non-pilot saves leave the column at its DB default (LEGACY) so the
    // saved data is identical to pre-migration.
    if (App.useGeocodingProxy()) {
        currentDataObject.addressVerificationStatus = 'MANUAL';
    }
    if (locationField && focusOnLocationField) {
        locationField.focus();
    }
}

function fillInAddress(locationResultObj: any, currentDataObject, locationField: HTMLInputElement | null = null,
    dataObjectLocationFields: DataObjectLocationFieldsType, focusOnLocationField: boolean = true) {
    const place = locationResultObj.geocoderResult;

    //get map of component we're interested in to its value
    const componentsOfInterest = { "street_number": null, "route": null, "locality": null, "sublocality_level_1": null, "administrative_area_level_1": null, "postal_code": null, "country": null };
    for (let i = 0; i < place.address_components.length; i++) {
        const addressTypes = place.address_components[i].types;
        for (const addressType of addressTypes) {
            if (componentsOfInterest.hasOwnProperty(addressType)) {
                componentsOfInterest[addressType] = place.address_components[i][componentForm[addressType]];
                break;
            }
        }
    }
    //save the components we're interested in to the database
    //Sets address component for data object. e.g.currentDataObject is a callCard, and dataObjectLocationFields.address.address is 'callLocAddress'
    if (dataObjectLocationFields.address.address) {
        currentDataObject[dataObjectLocationFields.address.address] = cleanArray([componentsOfInterest["street_number"], componentsOfInterest["route"]]).join(" ");
    }
    if (dataObjectLocationFields.address.city) {
        if (componentsOfInterest["locality"] !== null && componentsOfInterest["locality"] !== undefined) {
            currentDataObject[dataObjectLocationFields.address.city] = componentsOfInterest["locality"];
        } else {
            currentDataObject[dataObjectLocationFields.address.city] = componentsOfInterest["sublocality_level_1"];
        }
    }
    if (dataObjectLocationFields.address.state) {
        currentDataObject[dataObjectLocationFields.address.state] = componentsOfInterest["administrative_area_level_1"];
    }
    if (dataObjectLocationFields.address.zip) {
        currentDataObject[dataObjectLocationFields.address.zip] = componentsOfInterest["postal_code"];
    }
    if (dataObjectLocationFields.address.country) {
        currentDataObject[dataObjectLocationFields.address.country] = componentsOfInterest["country"];
    }

    //show formatted address on the form
    if (locationResultObj.locationType === "location") {
        //we are working with coordinates
        const coords = locationResultObj.locationCoords;
        const formattedCoords: string = `${coords.lat}, ${coords.lng}`;
        currentDataObject[dataObjectLocationFields.address.formattedAddress] = formattedCoords;
        if (locationField) locationField.value = formattedCoords;
    } else {
        //we are working with an address
        //Todo: Fix: Only the first of these two lines should be required, but both seem to be required to set formatted address for all entities.
        currentDataObject[dataObjectLocationFields.address.formattedAddress] = place.formatted_address;
        if (locationField) locationField.value = place.formatted_address;
    }

    //Save coordinates if applicable
    if (dataObjectLocationFields.properties.isOnlySavingCoordinatesOnCustomLocation) {
        currentDataObject[dataObjectLocationFields.coordinates.latitude] = null;
        currentDataObject[dataObjectLocationFields.coordinates.longitude] = null;
    } else {
        //set latitude and longitude e.g. for viewing on map
        if (locationResultObj.locationType === 'location') {
            //TODO: Check if this is ever called
            const coords = locationResultObj.locationCoords;
            currentDataObject[dataObjectLocationFields.coordinates.latitude] = coords.lat;
            currentDataObject[dataObjectLocationFields.coordinates.longitude] = coords.lng;
        } else {
            const latLngLocation = place.geometry.location;
            currentDataObject[dataObjectLocationFields.coordinates.latitude] = Number(latLngLocation.lat());
            currentDataObject[dataObjectLocationFields.coordinates.longitude] = Number(latLngLocation.lng());
        }
    }
    currentDataObject[dataObjectLocationFields.customlocation.customLocationId] = null;

    // Persist the geocode confidence onto the entity so the backend can store HIGH/LOW/MANUAL.
    // The confidence is attached to the resultsObj by geocodeLocation() on the proxy path.
    // On the legacy Google path it'll be undefined, leaving the entity's existing/default value.
    // If the user dragged the pin or picked an alternate, the inline preview tags the result
    // with `userAdjusted=true` so the status becomes MANUAL rather than LOW. A call pinned to the
    // operating-area centre (`pinnedToArea`) becomes AREA.
    const rawConfidence: string | undefined = (locationResultObj as any).confidence;
    // Defensive lowercase — see note in translateProxyResponseToGoogleShape.
    const confidence: string | null = rawConfidence != null ? String(rawConfidence).toLowerCase() : null;
    const userAdjusted: boolean = (locationResultObj as any).userAdjusted === true;
    let keepsLearnedAddress = false;
    if (confidence != null) {
        let status: string;
        if (userAdjusted) {
            status = 'MANUAL';
        } else if ((locationResultObj as any).pinnedToArea === true) {
            status = 'AREA';
        } else if (confidence === 'high') {
            status = 'HIGH';
        } else {
            status = 'LOW';
        }
        currentDataObject.addressVerificationStatus = status;
        keepsLearnedAddress = status === 'HIGH';
    }
    // The link holds only for the learned store's own unadjusted answer.
    setLearnedAddressId(currentDataObject, keepsLearnedAddress ? ((locationResultObj as any).learnedAddressId ?? null) : null);

    if (locationField && focusOnLocationField) {
        locationField.focus();
    }
}

// ── Info Window HTML ────────────────────────────────────────────────────────

function getCallCardMarkerContent(callCard: any, companyList: any, persons: any): string {
    const natureOfCallValues: string = callCard.natureOfCallValues.join(", ");
    const isPendingCall: boolean = callCard.primaryOfficerId === null || callCard.primaryOfficerId === undefined;

    let formattedAddress: string = callCard.callLocFormattedAddress;
    if (formattedAddress === null || formattedAddress === undefined) {
        formattedAddress = "N/A";
    }

    const badgeClass = isPendingCall ? "info-pending-badge" : "info-active-badge";
    const badgeText = isPendingCall ? "Pending" : "Active";

    let content: string = `<div class="map-info-window">`;
    content += `<div class="info-header">`;
    content += `<span class="${badgeClass}">${badgeText}</span>`;
    content += `<h2>${natureOfCallValues}</h2></div>`;
    content += `<div class="info-content">`;
    content += `<div class="info-event-id">${callCard.eventId}</div>`;
    content += `<div class="info-address">${formattedAddress}</div>`;
    const certainty = pinCertaintyForStatus(callCard.addressVerificationStatus);
    if (certainty) {
        const cls = certainty === 'area' ? 'onscene-status--area' : 'onscene-status--approx';
        content += `<div class="info-address ${cls}">${certainty === 'area' ? AREA_PIN_LABEL : APPROXIMATE_PIN_LABEL}</div>`;
    }

    // Only show Primary Unit section if an officer is assigned
    if (!isPendingCall) {
        content += `<div class="info-officer">`;
        content += `<div class="info-section-label">Primary Unit</div>`;
        content += `<span class="info-officer-name">${callCard.primaryOfficerName}</span>`;
        content += `<span class="info-status ${getOfficerStatusClass(callCard.primaryOfficerStatus)}">${convertOfficerStatusIdToOfficerStatus(callCard.primaryOfficerStatus)}</span>`;
        content += `</div>`;
    }

    if (callCard.backupUnitList !== null && callCard.backupUnitList !== undefined && callCard.backupUnitList.length > 0) {
        content += `<div class="info-officer">`;
        content += `<div class="info-section-label">Backup Units</div>`;
        content += `<div class="info-backup-list">`;
        for (let backupUnit of callCard.backupUnitList) {
            content += `<div class="info-officer">`;
            content += `<span class="info-officer-name">${backupUnit.officerName}</span>`;
            content += `<span class="info-status ${getOfficerStatusClass(backupUnit.officerStatus)}">${convertOfficerStatusIdToOfficerStatus(backupUnit.officerStatus)}</span>`;
            content += `</div>`;
        }
        content += `</div></div>`;
    }
    content += `</div>`;
    content += `<div class="info-footer">`;
    content += `<button id="mapViewCall" data-callcard-id="${callCard.id}" class="info-btn info-btn-primary" type="button">View Call</button>`;
    if (getModuleType() === "mdt") {
        content += `<button id="mapNavigation" data-callcard-id="${callCard.id}" data-callcard-coords="${callCard.latitude};${callCard.longitude}" class="info-btn info-btn-primary" type="button">Navigation</button>`;
    }
    content += `</div></div>`;
    return content;
}

function getTourStopMarkerContent(tourStop: any, isClaimedByMe: boolean): string {
    let content: string = `<div class="map-info-window">`;
    content += `<div class="info-header"><h2>${tourStop.title}</h2></div>`;
    content += `<div class="info-content">`;
    content += `<div class="info-address">${tourStop.location}</div>`;
    content += `<div class="info-tour-detail"><span>Client Name: </span><strong>${tourStop.npsdClientName}</strong></div>`;
    if (tourStop.npsdClientSiteNames) {
        content += `<div class="info-tour-detail"><span>Client Sites: </span><strong>${tourStop.npsdClientSiteNames}</strong></div>`;
    }
    if (tourStop.isClaimed) {
        content += `<div class="info-success">Claimed!</div>`;
    }
    if (tourStop.isCompleted) {
        content += `<div class="info-success">Completed!</div>`;
    }
    content += `</div>`;
    content += `<div class="info-footer">`;
    const buttonText = tourStop.isClaimed === false ? "Claim" : "View";
    content += `<button id="btnCompleteMapTour" data-guardTourStop-id="${tourStop.guardTourStopId}" data-guardTour-id="${tourStop.guardTourId}" class="info-btn info-btn-primary" type="button">${buttonText}</button>`;
    content += `</div></div>`;
    return content;
}

// ── Status Utilities ────────────────────────────────────────────────────────

function convertOfficerStatusIdToOfficerStatus(officerStatusId): string {
    if (officerStatusId === null || officerStatusId === undefined) {
        return "N/A";
    }
    const officerStatusIdToOfficerStatusObj = { "100": "Cleared", "200": "On-Scene", "300": "Enroute", "400": "Dispatched" };
    const officerStatus: string = officerStatusIdToOfficerStatusObj[officerStatusId.toString()];
    if (officerStatus === null || officerStatus === undefined) {
        return "N/A";
    } else {
        return officerStatus;
    }
}

function getOfficerStatusClass(officerStatusId): string {
    if (officerStatusId === null || officerStatusId === undefined) {
        return "info-status-na";
    }
    const statusClassMap = {
        "100": "info-status-cleared",
        "200": "info-status-onscene",
        "300": "info-status-enroute",
        "400": "info-status-dispatched"
    };
    return statusClassMap[officerStatusId.toString()] || "info-status-na";
}

function getYouMapMarkerIconPath(): string {
    return "/Content/Images/Map/you.svg";
}

function getPersonMarkerIcon(isYou: boolean, person: any): string {
    if (isYou) {
        return getYouMapMarkerIconPath();
    } else if (person.callCard === null) {
        return "/Content/Images/Map/available_officer.svg";
    } else {
        return "/Content/Images/Map/on_call_officer.svg";
    }
}

/**
 * Style a live board call marker. Overlapping calls keep the shared multi-call icon; a single
 * call with an uncertain pin uses uncertainPinStyle, keeping its A (active) / P (pending) letter.
 */
function applyCallCardMarkerIcon(marker: any, spiderfiedStatus: any): void {
    const spiderfiable = spiderfiedStatus == (window as any).OverlappingMarkerSpiderfier.markerStatus.SPIDERFIABLE;
    const certainty = spiderfiable ? null : pinCertaintyForStatus(marker.callCard?.addressVerificationStatus);
    if (certainty) {
        const style = uncertainPinStyle(certainty, marker.callCard.primaryOfficerId !== null ? 'A' : 'P');
        marker.setIcon(style.icon);
        marker.setLabel(style.label);
        marker.setTitle(`${marker.callCard.eventId} · ${style.title}`);
        return;
    }
    marker.setIcon({ url: getCallCardMarkerIcon(marker, spiderfiedStatus) });
    marker.setLabel(null);
    if (marker.callCard) marker.setTitle(marker.callCard.eventId);
}

function getCallCardMarkerIcon(marker: any, spiderfiedStatus: any): string {
    if (!marker.callCard) {
        return null;
    }
    if (spiderfiedStatus == (window as any).OverlappingMarkerSpiderfier.markerStatus.SPIDERFIABLE) { //if call cards are overlapping
        return "/Content/Images/Map/mixed_call_multi.png";
    } else {
        if (marker.callCard.primaryOfficerId !== null) { //if an officer is on this call card
            return "/Content/Images/Map/active_call.png";
        } else {
            return "/Content/Images/Map/pending_call.png";
        }
    }
}

function getTourStopMarkerIcon(isClaimed: boolean, isCompleted: boolean) {
    if (isCompleted)
        return "/Content/Images/Map/guard_tour_stop_completed.png";
    else if (isClaimed)
        return "/Content/Images/Map/guard_tour_stop_claimed.png";
    else if (isClaimed === false)
        return "/Content/Images/Map/guard_tour_stop_unclaimed.png";
    else
        return "";
}

// ── No-ops ──────────────────────────────────────────────────────────────────

function updateOfficerInfoWindowCSS() {
    // Info window styling now handled by CSS in map-info-window.css
}

function updateInfoWindowCSS(): void {
    // Info window styling now handled by CSS in map-info-window.css
}

// ── Marker Management ───────────────────────────────────────────────────────

function setOfficerAndCallCardLocations(resultsMap: any, centerMapOnYou: boolean, getNewData: boolean, authTokenRefresh: boolean) {
    const getMapListsInner = results => {
        if (CurrentMap.markerSpiderfier) {
            CurrentMap.markerSpiderfier.removeAllMarkers();
        } else {
            CurrentMap.markerSpiderfier = new (window as any).OverlappingMarkerSpiderfier(CurrentMap.map,
                {
                    markersWontMove: true,
                    markersWontHide: false,
                    basicFormatEvents: false,
                    keepSpiderfied: true,
                    nearbyDistance: 10,
                    circleFootSeparation: 20,
                    spiralFootSeparation: 20
                });
        }

        let personIdToTrueObj = {}; //use to find removed people

        let foundCenter: boolean = false;
        let currentUserId: number = getCurrentUserId();
        let showOfficersAvailable: boolean = (document.getElementById("mapFilterOfficersAvailable") as HTMLInputElement)?.checked ?? false;
        let showOfficersOnCall: boolean = (document.getElementById("mapFilterOfficersOnCall") as HTMLInputElement)?.checked ?? false;
        if (CurrentMap.markerClusterer) {
            CurrentMap.markerClusterer.clearMarkers();
        }

        for (let person of results.personList) {
            var company = results.companyList.find(n => n.id === person.companyId);
            personIdToTrueObj[person.id.toString()] = true;
            if (person.latitude === null || person.longitude === null) {
                continue;
            }
            let isYou: boolean = false;
            if (+person.id === currentUserId) {
                isYou = true;
            }

            let prevMarkerAndInfoWindow = CurrentMap.personIdToPersonMarkerAndInfoWindowDict[person.id.toString()];
            if (isYou || (showOfficersAvailable && person.callCard === null) || (showOfficersOnCall && person.callCard !== null)) {
                const markerPosition = {
                    lat: person.latitude,
                    lng: person.longitude
                };
                const markerTitle = person.name;
                const infoWindowContent: string = `<div class="map-info-window info-officer-card"><div class="info-content"><div class="info-officer-name">${person.name}</div><div class="info-company">${company.name}</div></div></div>`;
                // Removed ${isYou ? "(You)" : ""} from the above line to not show (You) in brackets
                var markerIcon = {
                    url: getPersonMarkerIcon(isYou, person),
                    anchor: new google.maps.Point(10, 15)
                };

                if (prevMarkerAndInfoWindow === null || prevMarkerAndInfoWindow === undefined) {
                    let marker = new google.maps.Marker({
                        position: markerPosition,
                        title: markerTitle,
                        icon: markerIcon
                    });
                    let infowindow = new google.maps.InfoWindow({
                        content: infoWindowContent
                    });
                    marker.addListener("click", () => {
                        infowindow.open(resultsMap, marker);
                        updateOfficerInfoWindowCSS();
                    });

                    if (getModuleType() === "mdt" || (getModuleType() === "cad" && isYou === false))
                        marker.setMap(CurrentMap.map);

                    CurrentMap.personIdToPersonMarkerAndInfoWindowDict[person.id.toString()] = { marker: marker, infowindow: infowindow };
                } else {
                    let prevMarker = prevMarkerAndInfoWindow.marker;
                    prevMarker.setPosition(markerPosition);
                    prevMarker.title = markerTitle;
                    prevMarker.icon = markerIcon;
                    if (prevMarker.getMap() !== resultsMap) {
                        if (getModuleType() === "mdt" || (getModuleType() === "cad" && isYou === false))
                            prevMarker.setMap(CurrentMap.map);
                    }
                    prevMarkerAndInfoWindow.infowindow.setContent(infoWindowContent);
                    updateOfficerInfoWindowCSS();
                }

                if (centerMapOnYou && isYou) {
                    foundCenter = true;
                    currentMapSetCenter(markerPosition);
                }
            } else { //filters exclude person, don't show person's marker
                if (prevMarkerAndInfoWindow !== null && prevMarkerAndInfoWindow !== undefined) {
                    CurrentMap.markerSpiderfier.removeMarker(prevMarkerAndInfoWindow.marker);
                    prevMarkerAndInfoWindow.infowindow.close();
                }
            }
        }

        //remove person markers that are not in personList
        for (let personId in CurrentMap.personIdToPersonMarkerAndInfoWindowDict) {
            if (personIdToTrueObj[personId] !== true) {
                let prevMarkerAndInfoWindow = CurrentMap.personIdToPersonMarkerAndInfoWindowDict[personId];
                if (prevMarkerAndInfoWindow !== null && prevMarkerAndInfoWindow !== undefined) {
                    CurrentMap.markerSpiderfier.removeMarker(prevMarkerAndInfoWindow.marker);
                    prevMarkerAndInfoWindow.infowindow.close();
                }
            }
        }

        let callCardIdToTrueObj = {}; //use to find removed call Cards
        let showCallCardsActive: boolean = (document.getElementById("mapFilterCallCardsActive") as HTMLInputElement)?.checked ?? false;
        let showCallCardsPending: boolean = (document.getElementById("mapFilterCallCardsPending") as HTMLInputElement)?.checked ?? false;
        let activeAndPendingCallCardMarkers = [];

        for (let callCard of results.callCardList) {
            callCardIdToTrueObj[callCard.id.toString()] = true;
            if (callCard.latitude === null || callCard.longitude === null) {
                continue;
            }
            const markerPosition = {
                lat: callCard.latitude,
                lng: callCard.longitude
            };
            const markerTitle: string = callCard.eventId;

            const infoWindowContent: string = getCallCardMarkerContent(callCard, results.companyList, results.personList);

            let prevMarkerAndInfoWindow = CurrentMap.callCardIdToCallCardMarkerAndInfoWindowDict[callCard.id.toString()];

            if ((showCallCardsActive && callCard.primaryOfficerId !== null) || (showCallCardsPending && callCard.primaryOfficerId === null)) {
                if (prevMarkerAndInfoWindow === null || prevMarkerAndInfoWindow === undefined) {
                    let marker = new google.maps.Marker({
                        position: markerPosition,
                        title: markerTitle,
                        draggable: getModuleType() === "cad"
                    });
                    marker["callCard"] = callCard;

                    activeAndPendingCallCardMarkers.push(marker);
                    let infowindow = new google.maps.InfoWindow({
                        content: infoWindowContent
                    });

                    marker.addListener("spider_click", () => {
                        infowindow.open(resultsMap, marker);
                        updateInfoWindowCSS();
                    });

                    if (getModuleType() === "cad") {
                        marker.addListener("dragstart", event => {
                            CurrentMap.pauseRefresh = true;
                            CurrentMap.originalMarkerPosition = event.latLng;
                        });

                        marker.addListener("dragend", event => {
                            CurrentMap.pauseRefresh = false;
                            CurrentMap.mapController!.pendingCallLocationChange = { marker, event };
                            CurrentMap.mapController!.showChangeCallLocationModal = true;
                        });
                    }

                    google.maps.event.addListener(marker, "spider_format", status => {
                        applyCallCardMarkerIcon(marker, status);
                    });
                    CurrentMap.markerSpiderfier.addMarker(marker, e => {
                    });

                    CurrentMap.callCardIdToCallCardMarkerAndInfoWindowDict[callCard.id.toString()] = { marker: marker, infowindow: infowindow };
                } else {
                    let prevMarker = prevMarkerAndInfoWindow.marker;
                    prevMarker.setPosition(markerPosition);
                    prevMarker.title = markerTitle;
                    // Refresh the call card reference so getCallCardMarkerIcon
                    // (called from the spider_format listener) sees up-to-date
                    // status when it next fires (e.g. on the next addMarker /
                    // formatMarkers cycle), rather than the stale snapshot from
                    // when the marker was originally created.
                    prevMarker["callCard"] = callCard;
                    activeAndPendingCallCardMarkers.push(prevMarker);
                    if (prevMarker.getMap() !== resultsMap) {
                        CurrentMap.markerSpiderfier.addMarker(prevMarker, e => {
                        });
                    }
                    prevMarkerAndInfoWindow.infowindow.setContent(infoWindowContent);
                    updateInfoWindowCSS();
                }
            } else { //don't show marker
                if (prevMarkerAndInfoWindow !== null && prevMarkerAndInfoWindow !== undefined) {
                    CurrentMap.markerSpiderfier.removeMarker(prevMarkerAndInfoWindow.marker);
                    prevMarkerAndInfoWindow.infowindow.close();
                }
            }
        }

        //remove call card markers that are not in callCardList
        for (let callCardId in CurrentMap.callCardIdToCallCardMarkerAndInfoWindowDict) {
            if (callCardIdToTrueObj[callCardId] !== true) {
                let prevMarkerAndInfoWindow = CurrentMap.callCardIdToCallCardMarkerAndInfoWindowDict[callCardId];
                if (prevMarkerAndInfoWindow !== null && prevMarkerAndInfoWindow !== undefined) {
                    CurrentMap.markerSpiderfier.removeMarker(prevMarkerAndInfoWindow.marker);
                    prevMarkerAndInfoWindow.infowindow.close();
                }
            }
        }

        // add tourstops
        let ShowCompleteGuardTours: boolean = (document.getElementById("mapFilterGuardToursComplete") as HTMLInputElement)?.checked ?? false;
        let ShowClaimedGuardTours: boolean = (document.getElementById("mapFilterGuardToursClaimed") as HTMLInputElement)?.checked ?? false;
        let ShowUnClaimedGuardTours: boolean = (document.getElementById("mapFilterGuardToursUnClaimed") as HTMLInputElement)?.checked ?? false;

        let isClaimedByMe = results.tourStopList.filter(n => n.isClaimedByMe).length > 0;

        for (let index in results.tourStopList) {
            let tourStop = results.tourStopList[index];

            if (!ShowCompleteGuardTours && tourStop.isCompleted)
                continue;

            if (!ShowClaimedGuardTours && tourStop.isClaimed && tourStop.isCompleted === false)
                continue;

            if (ShowUnClaimedGuardTours === false && tourStop.isClaimed === false)
                continue;

            const infoWindowContent: string = getTourStopMarkerContent(tourStop, isClaimedByMe);
            let prevMarkerAndInfoWindow = CurrentMap.tourStopIdToTourStopMarkerAndInfoWindowDict[tourStop.tourStopId.toString()];

            const markerPosition = {
                lat: tourStop.locationLatitude,
                lng: tourStop.locationLongitude
            };

            if (prevMarkerAndInfoWindow === null || prevMarkerAndInfoWindow === undefined) {
                let marker = new google.maps.Marker({
                    position: markerPosition,
                    title: tourStop.title
                });

                let infowindow = new google.maps.InfoWindow({
                    content: infoWindowContent
                });

                marker.addListener("spider_click", () => {
                    infowindow.open(resultsMap, marker);
                    updateInfoWindowCSS();
                });

                marker.setIcon({
                    url: getTourStopMarkerIcon(tourStop.isClaimed, tourStop.isCompleted)
                });

                CurrentMap.markerSpiderfier.addMarker(marker, e => {
                });

                CurrentMap.tourStopIdToTourStopMarkerAndInfoWindowDict[tourStop.tourStopId.toString()] = { marker: marker, infowindow: infowindow };
            }
            else {
                let prevMarker = prevMarkerAndInfoWindow.marker;
                prevMarker.setPosition(markerPosition);
                prevMarker.title = tourStop.title;

                prevMarker.icon = getTourStopMarkerIcon(tourStop.isClaimed, tourStop.isCompleted);

                if (prevMarker.getMap() !== resultsMap) {
                    CurrentMap.markerSpiderfier.addMarker(prevMarker, e => {
                    });
                }
                prevMarkerAndInfoWindow.infowindow.setContent(infoWindowContent);
                updateInfoWindowCSS();
            }
        }

        CurrentMap.markerClusterer = new MarkerClusterer({
            map: resultsMap,
            markers: activeAndPendingCallCardMarkers,
            algorithm: new GridAlgorithm({
                maxZoom: CurrentMap.MARKER_CLUSTERER_MAX_ZOOM,
                gridSize: 50
            }),
        });

        //failsafe for if "you" is not in the list of officers but we need to center on you
        if (centerMapOnYou && !foundCenter) {
            centerMapOnCurrentLocation();
        }
    };

    // Use this pause if a call is being dragged or if we are doing a previous API call to get the map lists
    if (CurrentMap.pauseRefresh === false || CurrentMap.isDoingMapListsApiCall === true) {
        if (getNewData || CurrentMap.mapCachedResults === null || CurrentMap.mapCachedResults === undefined) {
            const noLoadScreen: boolean = !authTokenRefresh;
            CurrentMap.isDoingMapListsApiCall = true;
            api.getMapLists(authTokenRefresh, noLoadScreen, results => {
                CurrentMap.isDoingMapListsApiCall = false;
                CurrentMap.mapCachedResults = results;
                getMapListsInner(CurrentMap.mapCachedResults);
            }, error => {
                CurrentMap.isDoingMapListsApiCall = false;
            });
        } else {
            getMapListsInner(CurrentMap.mapCachedResults);
        }
    }
}

// ── Zoom / Idle Handlers ────────────────────────────────────────────────────

function mapZoomChanged() {
    const curZoom = CurrentMap.map.getZoom();

    if (CurrentMap.map && CurrentMap.mapPrevZoom <= CurrentMap.MARKER_CLUSTERER_MAX_ZOOM && curZoom > CurrentMap.MARKER_CLUSTERER_MAX_ZOOM) {
        CurrentMap.triggerFormatMarkers = true;
        CurrentMap.markerSpiderfier?.formatMarkers();
    }

    // Scale MapLabels based on zoom level
    if (CurrentMap.polygonOverlays && CurrentMap.polygonOverlays.length > 0) {
        const sizes = calculateMapLabelSizes(curZoom);

        CurrentMap.polygonOverlays.forEach(polygonOverlay => {
            const label = polygonOverlay.label;
            label.set('fontSize', sizes.fontSize);
            label.set('iconSize', sizes.iconSize);
        });
    }

    CurrentMap.mapPrevZoom = curZoom;
}

function mapIdle() {
    if (CurrentMap.triggerFormatMarkers && CurrentMap.markerSpiderfier) {
        CurrentMap.markerSpiderfier.formatMarkers();
        CurrentMap.triggerFormatMarkers = false;
    }
}

// ── Map Initialization (Google Maps + Search Box) ───────────────────────────

/**
 * Returns the Google Maps style array matching the user's current theme.
 * Shared by every `new google.maps.Map(...)` call site so dialog-hosted maps
 * match the app theme at mount.
 */
function getMapStyleForCurrentTheme() {
    const themeColor = getCurrentThemeColor();

    if (themeColor === THEME_NIGHT_BLUE_COOKIE_VALUE) {
        //night blue
        return MIDNIGHT_MAP_STYLE;
    } else if (themeColor === THEME_NIGHT_GREY_COOKIE_VALUE) {
        //night grey
        return GOOGLE_NIGHT_MAP_STYLE;
    }
    //day
    return TEMP_MAP_STYLE;
}

function initAutocompleteTemp() {
    const mapStyle = getMapStyleForCurrentTheme();

    CurrentMap.map = new google.maps.Map(document.getElementById("map"), {
        zoom: INITIAL_MAP_ZOOM_LEVEL,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        styles: mapStyle
    });

    if (getModuleType() === "cad") {
        google.maps.event.addListener(
            CurrentMap.map,
            "rightclick",
            event => {
                mapRightClickedInCad(event.latLng);
            });

        google.maps.event.addListener(
            CurrentMap.map,
            "click",
            event => {
                mapLeftClickedOrDraggedInCad();
            });

        google.maps.event.addListener(
            CurrentMap.map,
            "drag",
            event => {
                mapLeftClickedOrDraggedInCad();
            });
    }

    // Create the search box and link it to the UI element.
    const input: any = document.getElementById("pac-input");
    const searchBox = new google.maps.places.SearchBox(input);
    CurrentMap.map.controls[google.maps.ControlPosition.TOP_RIGHT].push(input);

    // Bias the SearchBox results towards current map's viewport.
    CurrentMap.map.addListener("bounds_changed", () => {
        searchBox.setBounds(CurrentMap.map.getBounds());
    });

    CurrentMap.map.addListener("zoom_changed", mapZoomChanged);
    CurrentMap.map.addListener("idle", mapIdle);

    let markers = [];

    // Listen for the event fired when the user selects a prediction and retrieve
    // more details for that place.
    searchBox.addListener("places_changed", () => {
        const places = searchBox.getPlaces();

        if (places.length === 0) {
            return;
        }

        // Clear out the old markers.
        markers.forEach(marker => {
            marker.setMap(null);
        });
        markers = [];

        // For each place, get the icon, name and location.
        var bounds = new google.maps.LatLngBounds();
        places.forEach(place => {
            if (!place.geometry) {
                console.log("Returned place contains no geometry");
                return;
            }
            const icon = {
                url: place.icon,
                anchor: new google.maps.Point(10, 20),
                scaledSize: new google.maps.Size(25, 25)
            };

            // Create a marker for each place.
            markers.push(new google.maps.Marker({
                map: CurrentMap.map,
                icon: icon,
                title: place.name,
                position: place.geometry.location
            }));

            if (place.geometry.viewport) {
                // Only geocodes have viewport.
                bounds.union(place.geometry.viewport);
            } else {
                bounds.extend(place.geometry.location);
            }
        });
        CurrentMap.map.fitBounds(bounds);
    });

    // on movement make sure the map stops re-centering
    CurrentMap.map.addListener("drag", e => {
        const btnTrackMe = document.getElementById("btnTrackMe");
        if (btnTrackMe) {
            btnTrackMe.classList.remove("btn-success");
            btnTrackMe.classList.add("btn-danger");
        }
        //CurrentMap.centerMapOnYou = false;
    });

    ///// START Filters (traffic, officers, cards, stops) enabled/distabled //////

    CurrentMap.trafficLayer = new google.maps.TrafficLayer();
    CurrentMap.transitLayer = new google.maps.TransitLayer();
    CurrentMap.bikeLayer = new google.maps.BicyclingLayer();

    //Run in local scope so that trafficLayerValue, transitLayerValue, and bikeLayerValue aren't global.
    {
        const ornaMapFiltersJSON = localStorage.getItem("OrnaMapFilters");
        let ornaMapFilters: OrnaMapFiltersType;
        if (hasStringValue(ornaMapFiltersJSON)) {
            //Gets all Orna map filters, including for traffic/transit/bike layers, but also for
            // available officers, call cards, and guard tour stops.
            ornaMapFilters = JSON.parse(ornaMapFiltersJSON);
        } else {
            //Use defaults
            ornaMapFilters = getOrnaMapFiltersDefaults();
        }
        //Cache the filters for fast access
        CurrentMap.ornaMapFilters = ornaMapFilters;

        const trafficLayerValue: boolean = ornaMapFilters.isTrafficLayerEnabled;
        const transitLayerValue: boolean = ornaMapFilters.isTransitLayerEnabled;
        const bikeLayerValue: boolean = ornaMapFilters.isBikeLayerEnabled;

        const itemsToLoad = [
            { mapLayer: CurrentMap.trafficLayer, filterValue: trafficLayerValue },
            { mapLayer: CurrentMap.transitLayer, filterValue: transitLayerValue },
            { mapLayer: CurrentMap.bikeLayer, filterValue: bikeLayerValue },
        ]
        for (let item of itemsToLoad) {
            enableLayerForMap(item.mapLayer, item.filterValue);
        }

        updateMapFilterSwitches();
    }

    ///// END filters enabled/distabled //////
}

// ── Filter Persistence ──────────────────────────────────────────────────────

function getOrnaMapFiltersDefaults(): OrnaMapFiltersType {
    return {
        isTrafficLayerEnabled: false, isTransitLayerEnabled: false, isBikeLayerEnabled: false,
        areOfficersAvailableEnabled: true, areOfficersOnCallEnabled: true,
        areCallCardsActiveEnabled: true, areCallCardsPendingEnabled: true,
        areGuardTourStopsCompleteEnabled: true, areGuardTourStopsClaimedEnabled: true,
        areGuardTourStopsUnclaimedEnabled: true
    };
}

function updateMapFilterSwitches() {
    const itemsToLoad = [
        { switchId: 'trafficLayerSwitch', filterValue: CurrentMap.ornaMapFilters.isTrafficLayerEnabled },
        { switchId: 'transitLayerSwitch', filterValue: CurrentMap.ornaMapFilters.isTransitLayerEnabled },
        { switchId: 'bikeLayerSwitch', filterValue: CurrentMap.ornaMapFilters.isBikeLayerEnabled },

        { switchId: 'mapFilterOfficersAvailable', filterValue: CurrentMap.ornaMapFilters.areOfficersAvailableEnabled },
        { switchId: 'mapFilterOfficersOnCall', filterValue: CurrentMap.ornaMapFilters.areOfficersOnCallEnabled },

        { switchId: 'mapFilterCallCardsActive', filterValue: CurrentMap.ornaMapFilters.areCallCardsActiveEnabled },
        { switchId: 'mapFilterCallCardsPending', filterValue: CurrentMap.ornaMapFilters.areCallCardsPendingEnabled },

        { switchId: 'mapFilterGuardToursComplete', filterValue: CurrentMap.ornaMapFilters.areGuardTourStopsCompleteEnabled },
        { switchId: 'mapFilterGuardToursClaimed', filterValue: CurrentMap.ornaMapFilters.areGuardTourStopsClaimedEnabled },
        { switchId: 'mapFilterGuardToursUnClaimed', filterValue: CurrentMap.ornaMapFilters.areGuardTourStopsUnclaimedEnabled },
    ]
    for (let item of itemsToLoad) {
        const el = document.getElementById(item.switchId) as HTMLInputElement;
        if (el) {
            el.checked = item.filterValue;
        }
    }
}

function updateFiltersToCacheAndLocalStorage() {
    const itemsToSave = [
        { id: 'trafficLayerSwitch', filterName: 'isTrafficLayerEnabled' },
        { id: 'transitLayerSwitch', filterName: 'isTransitLayerEnabled' },
        { id: 'bikeLayerSwitch', filterName: 'isBikeLayerEnabled' },

        { id: 'mapFilterOfficersAvailable', filterName: 'areOfficersAvailableEnabled' },
        { id: 'mapFilterOfficersOnCall', filterName: 'areOfficersOnCallEnabled' },

        { id: 'mapFilterCallCardsActive', filterName: 'areCallCardsActiveEnabled' },
        { id: 'mapFilterCallCardsPending', filterName: 'areCallCardsPendingEnabled' },

        { id: 'mapFilterGuardToursComplete', filterName: 'areGuardTourStopsCompleteEnabled' },
        { id: 'mapFilterGuardToursClaimed', filterName: 'areGuardTourStopsClaimedEnabled' },
        { id: 'mapFilterGuardToursUnClaimed', filterName: 'areGuardTourStopsUnclaimedEnabled' },
    ]

    const ornaMapFilters = getOrnaMapFiltersDefaults();
    for (let item of itemsToSave) {
        const el = document.getElementById(item.id) as HTMLInputElement;
        const isItemChecked: boolean = el?.checked ?? false;
        ornaMapFilters[item.filterName] = isItemChecked;
    }
    CurrentMap.ornaMapFilters = ornaMapFilters;
    localStorage.setItem("OrnaMapFilters", JSON.stringify(ornaMapFilters));
}

function enableLayerForMap(mapLayer: google.maps.TrafficLayer | google.maps.TransitLayer | google.maps.BicyclingLayer,
    enableLayer: boolean) {
    if (enableLayer) {
        mapLayer.setMap(CurrentMap.map); // show
    } else {
        mapLayer.setMap(null); // hide
    }
}

function layerSwitchClicked(switchId: string,
    mapLayer: google.maps.TrafficLayer | google.maps.TransitLayer | google.maps.BicyclingLayer) {
    const el = document.getElementById(switchId) as HTMLInputElement;
    const isLayerEnabled: boolean = el?.checked ?? false;
    enableLayerForMap(mapLayer, isLayerEnabled);
    updateFiltersToCacheAndLocalStorage();
}

// ── Context Menu ────────────────────────────────────────────────────────────

function getCanvasXY(caurrentLatLng) {
    const bounds = CurrentMap.map.getBounds();
    if (!bounds) return null;
    const scale = Math.pow(2, CurrentMap.map.getZoom());
    const nw = new google.maps.LatLng(
        bounds.getNorthEast().lat(),
        bounds.getSouthWest().lng()
    );
    const worldCoordinateNW = CurrentMap.map.getProjection().fromLatLngToPoint(nw);
    const worldCoordinate = CurrentMap.map.getProjection().fromLatLngToPoint(caurrentLatLng);
    const caurrentLatLngOffset = new google.maps.Point(
        Math.floor((worldCoordinate.x - worldCoordinateNW.x) * scale),
        Math.floor((worldCoordinate.y - worldCoordinateNW.y) * scale)
    );
    return caurrentLatLngOffset;
}

function setMenuXY(currentLatLng) {
    const clickedPosition = getCanvasXY(currentLatLng);
    if (!clickedPosition) return;
    const x = clickedPosition.x;
    const y = clickedPosition.y;

    const contextMenu = document.querySelector(".contextmenu") as HTMLElement;
    if (contextMenu) {
        contextMenu.style.left = x + "px";
        contextMenu.style.top = y + "px";
    }
}

function showContextMenu(currentLatLng) {
    // Remove existing context menus
    document.querySelectorAll(".contextmenu").forEach(el => el.remove());

    // Don't show context menu if map or bounds aren't ready yet
    if (!CurrentMap.map || !CurrentMap.map.getBounds()) return;

    const contextmenuDir = document.createElement("div");
    contextmenuDir.className = "contextmenu";
    contextmenuDir.style.display = 'inline-block';
    contextmenuDir.innerHTML = `<div class="k-button" style="background-color:#fff;width:100%;display:inline-block;text-align:left"><a id='menuNewCall'>Add Call</a></div>`;

    let submodules: Array<string> = getAvailableSubmodules();
    if (submodules === null) {
        submodules = [];
    }
    if (isInArray(submodules, AccessAuthorities.ROLE_CUSTOM_LOCATION_SAVE)) {
        const coordsString = currentLatLng.lat() + ',' + currentLatLng.lng();
        contextmenuDir.innerHTML += `<hr style="margin:0"/>` +
            `<div class="k-button" style="background-color:#fff;width:100%;display:inline-block;text-align:left"><a id='menuNewCallWithCustomLocation' data-coords=${coordsString}>Add Call with Custom Location</a></div>`;
    }

    CurrentMap.map.getDiv().appendChild(contextmenuDir);

    setMenuXY(currentLatLng);

    contextmenuDir.style.visibility = "visible";
}

// ── Document Click Handler ──────────────────────────────────────────────────

document.addEventListener("click", (e: MouseEvent) => {
    const target = e.target as HTMLElement;

    // #ddlMapFilter
    if (target.closest("#ddlMapFilter")) {
        setOfficerAndCallCardLocations(CurrentMap.map, false, false, true);
        return;
    }

    // #mapViewCall
    const mapViewCallEl = target.closest("#mapViewCall") as HTMLElement;
    if (mapViewCallEl) {
        const callCardId = mapViewCallEl.dataset.callcardId;
        window.OrnaReact.navigate.toCallCard(parseInt(callCardId));
        return;
    }

    // #mapNavigation
    const mapNavigationEl = target.closest("#mapNavigation") as HTMLElement;
    if (mapNavigationEl) {
        const coords = mapNavigationEl.dataset.callcardCoords.split(";");
        // An uncertain pin (LOW / AREA) is not the destination; navigate by the address text.
        const callCard = CurrentMap?.callCardIdToCallCardMarkerAndInfoWindowDict[mapNavigationEl.dataset.callcardId]?.marker?.callCard;
        const address = pinCertaintyForStatus(callCard?.addressVerificationStatus) ? (callCard.callLocFormattedAddress ?? null) : null;
        startTurnByTurnNavigationV2(parseFloat(coords[0]), parseFloat(coords[1]), address);
        return;
    }

    // #btnCompleteMapTour
    const btnCompleteMapTourEl = target.closest("#btnCompleteMapTour") as HTMLElement;
    if (btnCompleteMapTourEl) {
        const guardTourId = parseInt(btnCompleteMapTourEl.dataset.guardtourId, 10);
        const guardTourStopId = btnCompleteMapTourEl.dataset.guardtourstopId;

        if (btnCompleteMapTourEl.textContent === "Claim") {
            api.claimGuardTour(guardTourId, true, function (result) {
                if (result.status === "success") {
                    App.showPopupNotification("Tour claimed successfully.", "success");
                    window.OrnaReact.navigate.raw(`/?sectionName=${getModuleType().toUpperCase()}&tab=GuardToursNew&function=MDT_GuardToursNew`);
                } else {
                    App.showPopupNotification("Tour unclaimed successfully.", "success");
                }
            });
        } else {
            window.OrnaReact.navigate.raw(`/?sectionName=${getModuleType().toUpperCase()}&tab=GuardToursNew&function=showCompleteTourStopsForGuardTour&multiModule=True&params=${guardTourId}|${guardTourStopId}|false`);
        }
        return;
    }

    // #menuNewCall
    if (target.closest("#menuNewCall")) {
        window.OrnaReact.navigate.to({
            section: 'CAD',
            tab: 'NewCallCard',
            fn: 'CAD_CallCard',
            multiModule: true,
            state: {
                lat: CurrentMap.currentEvent.lat(),
                lng: CurrentMap.currentEvent.lng(),
            },
        });
        return;
    }

    // #menuNewCallWithCustomLocation
    const menuNewCallWithCustomLocationEl = target.closest("#menuNewCallWithCustomLocation") as HTMLElement;
    if (menuNewCallWithCustomLocationEl) {
        CurrentMap.mapController!.addCustomLocationDialogLocation = null;
        CurrentMap.mapController!.addCustomLocationDialogCoordinates = menuNewCallWithCustomLocationEl.dataset.coords;
        CurrentMap.mapController!.showAddCustomLocationModal = true;

        CurrentMap.mapController!.focusCustomLocationInput();
        return;
    }
});

// ── Window Registrations ────────────────────────────────────────────────────

// Entry points (called by routing)
(window as any).CAD_Map = CAD_Map;
(window as any).MDT_Map = MDT_Map;

// Cleanup
(window as any).mapCleanup = mapCleanup;
(window as any).clearMapRefresh = clearMapRefresh;
(window as any).updateMapFromEvent = updateMapFromEvent;

// Overlay management
(window as any).getPatrolZoneNames = getPatrolZoneNames;
(window as any).getAllPolygonLabelsAndIds = getAllPolygonLabelsAndIds;
(window as any).realtimeUpdateMapOverlay = realtimeUpdateMapOverlay;
(window as any).clearPolygonOverlayCache = clearPolygonOverlayCache;
(window as any).clearMapOverlayKmlCache = clearMapOverlayKmlCache;

// Polygon construction
(window as any).newGoogleMapsPolygon = newGoogleMapsPolygon;
(window as any).deselectAllPolygons = deselectAllPolygons;
(window as any).resetOverlayZIndexes = resetOverlayZIndexes;

// Map state helpers
(window as any).isMapScreenLoaded = isMapScreenLoaded;
(window as any).isMapCenterSet = isMapCenterSet;
(window as any).cleanUpMap = cleanUpMap;
(window as any).setMapScreenLoaded = setMapScreenLoaded;
(window as any).trackUser = trackUser;
(window as any).centerMapOnCurrentLocation = centerMapOnCurrentLocation;

// Geocoding / address
(window as any).geocodeLocation = geocodeLocation;
(window as any).fillInAddressFromSuggestions = fillInAddressFromSuggestions;
(window as any).fillInAddress = fillInAddress;
(window as any).clearAddressInfo = clearAddressInfo;
(window as any).isLatLongCoords = isLatLongCoords;

// Filter persistence
(window as any).updateFiltersToCacheAndLocalStorage = updateFiltersToCacheAndLocalStorage;
(window as any).layerSwitchClicked = layerSwitchClicked;

// Theming
(window as any).getMapStyleForCurrentTheme = getMapStyleForCurrentTheme;

// ── Exports ─────────────────────────────────────────────────────────────────

export {
    CurrentMap,
    mapCleanup,
    clearMapRefresh,
    updateMapFromEvent,
    getPatrolZoneNames,
    getAllPolygonLabelsAndIds,
    realtimeUpdateMapOverlay,
    clearPolygonOverlayCache,
    clearMapOverlayKmlCache,
    newGoogleMapsPolygon,
    deselectAllPolygons,
    resetOverlayZIndexes,
    isMapScreenLoaded,
    isMapCenterSet,
    cleanUpMap,
    setMapScreenLoaded,
    trackUser,
    centerMapOnCurrentLocation,
    geocodeLocation,
    fillInAddressFromSuggestions,
    fillInAddress,
    clearAddressInfo,
    isLatLongCoords,
    updateFiltersToCacheAndLocalStorage,
    layerSwitchClicked,
    getMapStyleForCurrentTheme,
    CAD_Map,
    MDT_Map,
    // Granular init functions for React MapScreen
    setupMapCore,
    initAutocompleteTemp,
    initMap,
    setUpMapOverlay,
    resetMapRefresh,
};
